import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/recent-sessions`.
 *
 * Last 10 sessions in the caller's scope, sorted by started_at
 * desc. Cheap query — used by the Recent Sessions dashboard
 * widget for at-a-glance "what just happened on my fleet."
 *
 * Distinct from the Active Sessions list (currently-charging
 * sessions) on `OperatorDashboardData.activeSessionsList`. This
 * widget shows recent HISTORICAL sessions including completed,
 * stopped, and failed ones.
 *
 * `is_scoped` drives empty-state copy. When true, the empty
 * state reads "No sessions at your assigned locations yet"
 * rather than the misleading "No sessions yet" (which would
 * imply the platform has never seen a session, when really
 * the issue is that none have happened in the caller's
 * AccessAssignment subset).
 */
export const RecentSessionStatusSchema = z.enum([
    'active', 'completed', 'failed', 'stopped_by_user', 'stopped_by_system',
]);
export const RecentSessionRowSchema = z.object({
    session_id: z.string(),
    /** Charger identity (e.g. "CU89TVWKPVJ8"). Falls back to a 6-char
     *  slug of the charger _id if the charger doc is missing. */
    charger_identity: z.string(),
    /** Location name. Null if location doc missing — UI falls back
     *  to "Unknown location". */
    location_name: z.string().nullable(),
    /** Display-name with privacy-aware fallbacks:
     *   - user attached      → "F. Last" (matches operator dashboard)
     *   - rfid_tag, no user  → "RFID · 1234" (last 4 of tag)
     *   - neither            → "Guest" */
    driver_name: z.string(),
    started_at: z.string(),
    status: RecentSessionStatusSchema,
    /** Session cost in cents. Zero for sessions that didn't bill. */
    total_cost_cents: z.number().int(),
    energy_kwh: z.number().nonnegative(),
});
export const RecentSessionsResponseSchema = z.object({
    rows: z.array(RecentSessionRowSchema),
    /** True when the caller is third_party_admin (or otherwise
     *  AccessAssignment-scoped). Drives empty-state copy. */
    is_scoped: z.boolean(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-recent-sessions.js.map